import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, TooltipProvider, cn } from "@nexus/ui";
import {
  MIN_SCORED_CREATIVES_FOR_INSIGHTS,
  type CriterionBlocker,
} from "@nexus/contracts";
import { weightPercent } from "../../lib/feature-format";
import { CollapsibleSection } from "../CollapsibleSection/CollapsibleSection";
import { CriterionLabel } from "../CriterionLabel/CriterionLabel";

function BlockerRow({
  blocker,
  rank,
  maxImpact,
  mounted,
}: {
  blocker: CriterionBlocker;
  rank: number;
  maxImpact: number;
  mounted: boolean;
}) {
  const { t } = useTranslation();
  const missed = blocker.failedCount + blocker.partialCount;

  // Bar length encodes *relative score impact* (weight × misses) so the worst
  // blocker reads longest and lesser ones taper — not a constantly-full miss rate.
  // The fill splits fail vs partial so composition is visible at a glance.
  const share =
    maxImpact > 0
      ? (blocker.impact / maxImpact) * 100
      : (missed / blocker.scoredAssets) * 100;
  const failShare = missed > 0 ? share * (blocker.failedCount / missed) : 0;
  const partialShare = missed > 0 ? share * (blocker.partialCount / missed) : 0;

  return (
    <li className="flex items-center gap-3">
      <span
        aria-hidden
        className="bg-muted text-muted-foreground flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums"
      >
        {rank}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-3">
          <CriterionLabel
            name={blocker.name}
            definition={blocker.definition}
            className="text-foreground min-w-0 text-sm font-medium"
          />
          <span className="shrink-0 text-xs tabular-nums">
            <span className="text-foreground font-medium">
              {t("preflight.blockers.missedOf", {
                count: missed,
                total: blocker.scoredAssets,
              })}
            </span>
            <span className="text-muted-foreground">
              {" · "}
              {t("preflight.blockers.weightLabel", {
                weight: weightPercent(blocker.weight, 0),
              })}
            </span>
          </span>
        </div>
        <div className="bg-muted flex h-2 w-full gap-px overflow-hidden rounded-full">
          <span
            className="bg-error h-full transition-[width] duration-500 ease-out motion-reduce:transition-none"
            style={{ width: mounted ? `${failShare}%` : "0%" }}
          />
          {blocker.partialCount > 0 ? (
            <span
              className="bg-warning h-full transition-[width] duration-500 ease-out motion-reduce:transition-none"
              style={{ width: mounted ? `${partialShare}%` : "0%" }}
            />
          ) : null}
        </div>
      </div>
    </li>
  );
}

export interface ReportTopBlockersProps {
  /** Already-ranked blocker rows — rendered in the order given, never re-ranked here. */
  blockers: readonly CriterionBlocker[];
  /** Scored creatives the rows were aggregated over. */
  scoredCount: number;
  /** Render just the panel body (no collapsible section) — used inside the insights tabs. */
  headless?: boolean;
  className?: string;
}

/**
 * Cross-creative synthesis: the criteria hurting scores most, ranked by weighted impact
 * upstream (one report's creatives, or every creative in a market). Renders nothing until
 * two creatives can be compared, or when nothing is failing.
 */
export function ReportTopBlockers({
  blockers,
  scoredCount,
  headless = false,
  className,
}: ReportTopBlockersProps) {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);

  const maxImpact = Math.max(0, ...blockers.map((blocker) => blocker.impact));

  useEffect(() => {
    const frame = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  if (scoredCount < MIN_SCORED_CREATIVES_FOR_INSIGHTS || blockers.length === 0)
    return null;

  const body = (
    <TooltipProvider delayDuration={250} skipDelayDuration={100}>
      <p className="text-muted-foreground mb-4 text-sm">
        {t("preflight.blockers.description", { count: scoredCount })}
      </p>
      <ol className="flex flex-col gap-4">
        {blockers.map((blocker, index) => (
          <BlockerRow
            key={blocker.code ?? blocker.name}
            blocker={blocker}
            rank={index + 1}
            maxImpact={maxImpact}
            mounted={mounted}
          />
        ))}
      </ol>
    </TooltipProvider>
  );

  if (headless) return body;

  return (
    <CollapsibleSection
      title={t("preflight.blockers.title")}
      className={className}
    >
      <Card className="gap-0 border-border/60 p-5">{body}</Card>
    </CollapsibleSection>
  );
}
